import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { FormField, FormItem, FormMessage } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { Plus } from "lucide-react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { McpCapabilityConfigSchema, TestMcpResponseSchema } from "schema"
import { z } from "zod"
import { FieldsForm } from "../FieldsForm"
import TestMCPConnection from "./TestMCPConnection"

type MCPCapabilityFormType = z.infer<typeof McpCapabilityConfigSchema>
type DiscoveredTool = z.infer<typeof TestMcpResponseSchema>["tools"][number]

type Props = {
  isLoading: boolean
  onToolsDiscovered?: (tools: Array<DiscoveredTool>) => void
  onTestStart?: () => void
}

export function MCPServerAccordion({ isLoading, onToolsDiscovered, onTestStart }: Props) {
  const { control, watch, formState } = useFormContext<MCPCapabilityFormType>()
  const { t } = useTranslation()

  const transportType = watch("transportType")
  const authType = watch("authConfig.type")

  const hasError = Boolean(
    formState.errors.serverUrl ||
    formState.errors.authConfig ||
    formState.errors.connectionTimeoutMs ||
    formState.errors.executionTimeoutMs,
  )

  return (
    <Accordion
      type="single"
      defaultValue="server"
      collapsible
      className={cn("border rounded-lg bg-secondary", hasError && "border-destructive")}
    >
      <AccordionItem value="server">
        <AccordionTrigger className="text-lg">
          {t("capabilities.mcpConfig.server.label")}
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-5">
          <p className="text-sm text-neutral-500">
            {t("capabilities.mcpConfig.server.description")}
          </p>
          <FormField
            control={control}
            name="transportType"
            render={({ field }) => (
              <FormItem>
                <Label>{t("capabilities.mcpConfig.transportType.label")}</Label>
                <div className="flex gap-0">
                  <Button
                    type="button"
                    variant={field.value === "streamable-http" ? "default" : "outline"}
                    className="flex-1 border-r-0 rounded-r-none"
                    onClick={() => field.onChange("streamable-http")}
                    disabled={isLoading}
                  >
                    {t("capabilities.mcpConfig.transportType.streamableHttp")}
                  </Button>
                  <Button
                    type="button"
                    variant={field.value === "sse" ? "default" : "outline"}
                    className="flex-1 rounded-l-none"
                    onClick={() => field.onChange("sse")}
                    disabled={isLoading}
                  >
                    {t("capabilities.mcpConfig.transportType.sse")}
                  </Button>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="serverUrl"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="serverUrl">{t("capabilities.mcpConfig.serverUrl.label")}</Label>
                <Input
                  {...field}
                  id="serverUrl"
                  placeholder={
                    transportType === "streamable-http"
                      ? t("capabilities.mcpConfig.serverUrl.placeholder.streamableHttp")
                      : t("capabilities.mcpConfig.serverUrl.placeholder.sse")
                  }
                  disabled={isLoading}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="authConfig.type"
            render={({ field }) => (
              <FormItem>
                <Label htmlFor="authType">
                  {t("capabilities.mcpConfig.authConfig.type.label")}
                </Label>
                <Select onValueChange={field.onChange} value={field.value} disabled={isLoading}>
                  <SelectTrigger id="authType">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      {t("capabilities.mcpConfig.authConfig.type.none")}
                    </SelectItem>
                    <SelectItem value="bearer">
                      {t("capabilities.mcpConfig.authConfig.type.bearer")}
                    </SelectItem>
                    <SelectItem value="apiKey">
                      {t("capabilities.mcpConfig.authConfig.type.apiKey")}
                    </SelectItem>
                    <SelectItem value="custom">
                      {t("capabilities.mcpConfig.authConfig.type.custom")}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          {authType === "bearer" && (
            <FormField
              control={control}
              name="authConfig.token"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="token">
                    {t("capabilities.mcpConfig.authConfig.token.label")}
                  </Label>
                  <Input
                    {...field}
                    id="token"
                    placeholder={t("capabilities.mcpConfig.authConfig.token.placeholder")}
                    disabled={isLoading}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          {authType === "apiKey" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={control}
                name="authConfig.headerName"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="headerName">
                      {t("capabilities.mcpConfig.authConfig.headerName.label")}
                    </Label>
                    <Input
                      {...field}
                      id="headerName"
                      placeholder={t("capabilities.mcpConfig.authConfig.headerName.placeholder")}
                      disabled={isLoading}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="authConfig.apiKey"
                render={({ field }) => (
                  <FormItem>
                    <Label htmlFor="apiKey">
                      {t("capabilities.mcpConfig.authConfig.apiKey.label")}
                    </Label>
                    <Input
                      {...field}
                      id="apiKey"
                      placeholder={t("capabilities.mcpConfig.authConfig.apiKey.placeholder")}
                      disabled={isLoading}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
          {authType === "custom" && (
            <FormField
              control={control}
              name="authConfig.customHeaders"
              render={({ field }) => (
                <FormItem>
                  <FieldsForm
                    label={t("capabilities.mcpConfig.authConfig.customHeaders.label")}
                    description={t("capabilities.mcpConfig.authConfig.customHeaders.description")}
                    value={
                      field.value
                        ? Object.entries(field.value).map(([name, value]) => ({
                            name,
                            value: value as string,
                            type: "string" as const,
                          }))
                        : []
                    }
                    onChange={(fields) => {
                      const headers = fields.reduce(
                        (acc, f) => {
                          if (f.name) acc[f.name] = f.value || ""
                          return acc
                        },
                        {} as Record<string, string>,
                      )
                      field.onChange(headers)
                    }}
                    mode="keyValue"
                    action={
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const current = field.value || {}
                          field.onChange({ ...current, "": "" })
                        }}
                        disabled={isLoading}
                      >
                        <Plus className="size-4" />
                        {t("capabilities.webhookConfig.fieldsForm.addField")}
                      </Button>
                    }
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={control}
              name="connectionTimeoutMs"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="connectionTimeoutMs">
                    {t("capabilities.mcpConfig.connectionTimeoutMs.label")}
                  </Label>
                  <Input {...field} id="connectionTimeoutMs" disabled={isLoading} />
                  <p className="text-sm text-muted-foreground">
                    {t("capabilities.mcpConfig.connectionTimeoutMs.description")}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={control}
              name="executionTimeoutMs"
              render={({ field }) => (
                <FormItem>
                  <Label htmlFor="executionTimeoutMs">
                    {t("capabilities.mcpConfig.executionTimeoutMs.label")}
                  </Label>
                  <Input {...field} id="executionTimeoutMs" disabled={isLoading} />
                  <p className="text-sm text-muted-foreground">
                    {t("capabilities.mcpConfig.executionTimeoutMs.description")}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <TestMCPConnection onToolsDiscovered={onToolsDiscovered} onTestStart={onTestStart} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}
